import React from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./styles/tokens.css";
import "./App.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Routes, Route, useLocation } from "react-router-dom";

import AuthPage from "./AuthPage";
import EmailVerified from "./EmailVerified";
import PasswordResetComplete from "./PasswordResetComplete";
import NotFound from "./NotFound";

import UserDashboard from "./UserDashboard";
import UserResultsPage from "./UserResultsPage";
import UserProfilePage from "./UserProfilePage";
import UpdateProfile from "./UpdateProfile";
import AboutApp from "./AboutApp";
import GDPRPage from "./GDPRPage";
import PaymentRace from "./PaymentRace";
import PaymentMembership from "./PaymentMembership";
import PdfViewer from "./PdfViewer";
import MapViewer from "./MapViewer";

// Obrazovka rozhodčího je záměrně v hlavním bundlu: aplikace nemá service
// worker, takže líně načítaný chunk by rozhodčí bez signálu na střelnici
// neotevřel – bodování offline musí fungovat, jakmile je aplikace načtená.
import JudgeView from "./JudgeView";

import { AuthProvider, RequireAuth, RequireRole } from "./utils/useAuth";
import { DISPLAY_CRASH_RETRY_MS } from "./utils/displaySettings";
import { ToastProvider } from "./ui/Toast";
import { ConfirmProvider } from "./ui/ConfirmDialog";
import { AppShell, OfflineOverlay, RouteErrorBoundary, RouteLoading, Screen } from "./ui/AppShell";
import { lazyRoute, usePreloadWhenIdle } from "./ui/lazyRoute";
import { AdminLiveResults, LIVE_SCREENS, LiveRaceSetup } from "./liveScreens";
import { loadXlsx } from "./utils/xlsx";
// Styly nastavení živého závodu zůstávají v hlavním CSS a na stejném místě
// (po ui.css) jako před líným načítáním: globální .lks-newbie-btn z nich
// používá i obrazovka rozhodčího (tlačítka nováčka) – v chunku by se jí
// vzhled změnil až po stažení nastavení, offline nikdy.
import "./liveSetup/liveSetup.css";

// ── Líně načítané obrazovky (úklid a rychlost, 12. 10. 2026) ──────────
// Stahují se až při prvním otevření; chybu stažení (offline) ukáže
// RouteErrorBoundary se „Zkusit znovu“ (ui/lazyRoute). Administrace je jeden
// chunk – po prvním otevření jdou všechny její obrazovky i bez připojení;
// střelnice zvlášť (Google Maps), XLSX se stahuje až při exportu/importu.
const AdminDashboard = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./AdminDashboard"));
const MembersList = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./MembersList"));
const Payments = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./Payments"));
const ManualPayments = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./ManualPaymentDashboard"));
const Races = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./Races"));
const MembershipPricing = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./MembershipPricing"));
const AdminRegistrations = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./AdminRegistrations"));
const AdminResultsUpload = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./AdminResultsUpload"));
const AdminShootOffs = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./AdminShootOffs"));
const AdminOverview = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./AdminOverview"));
const TargetsAdmin = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./TargetsAdmin"));
const AdminRehearsal = lazyRoute(() => import(/* webpackChunkName: "admin" */ "./AdminRehearsal"));
const ShootingRanges = lazyRoute(() => import(/* webpackChunkName: "admin-strelnice" */ "./ShootingRanges"));

// Živé bodování mimo obrazovku rozhodčího: nastavení (i hlavní rozhodčí na
// střelnici – nováček do startovky) a živé výsledky jsou v liveScreens.ts
// (stahuje je předem i spodní navigace rozhodčím), tabule tady.
const DisplayView = lazyRoute(() => import(/* webpackChunkName: "display" */ "./DisplayView"));

/** SheetJS pro export živých výsledků a rund – ať jde exportovat i offline. */
const XLSX_PRELOAD = [{ preload: () => loadXlsx().then(() => undefined) }];

/**
 * Kdo otevře živou obrazovku (rozhodčí, nastavení, živé výsledky), dostane
 * ostatní živé obrazovky stažené předem, až bude prohlížeč volný (a znovu po
 * návratu připojení) – na střelnici bez signálu se pak mezi nimi přepne
 * i offline (JudgeView vede na živé výsledky, ty na nastavení, nastavení
 * zpět na výsledky). Na výsledcích a nastavení i SheetJS pro export (dřív
 * byl v hlavním bundlu a export fungoval offline).
 */
const PreloadLiveScreens: React.FC<{ withXlsx?: boolean }> = ({ withXlsx = false }) => {
  usePreloadWhenIdle(LIVE_SCREENS);
  usePreloadWhenIdle(XLSX_PRELOAD, withXlsx);
  return null;
};

/** Živé výsledky si AppShell kreslí samy – stejný rám pro čekání a chybu. */
const liveResultsFrame = (content: React.ReactNode) => (
  <AppShell variant="admin" title="Živé výsledky" back="history" maxWidth={1600}>
    {content}
  </AppShell>
);

const LiveResultsRoute: React.FC = () => {
  const { pathname } = useLocation();
  return (
    <RouteErrorBoundary resetKey={pathname} frame={liveResultsFrame}>
      <React.Suspense fallback={liveResultsFrame(<RouteLoading />)}>
        <AdminLiveResults />
      </React.Suspense>
    </RouteErrorBoundary>
  );
};

/**
 * Obal pro obrazovky mimo AppShell (rozhodčí, živá tabule, 404): přesná
 * kopie dřívějšího kořenového wrapperu – safe-area přes env(), pozadí,
 * overflow hidden. /judge a /display se podle plánu nemění až do kroků 12–13.
 * Přihlášení a stavové stránky jsou od kroku 5 v AppShellu bez navigace.
 */
const Bare: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      paddingTop: "env(safe-area-inset-top)",
      paddingBottom: "env(safe-area-inset-bottom)",
      paddingLeft: "env(safe-area-inset-left)",
      paddingRight: "env(safe-area-inset-right)",
      backgroundColor: "var(--lks-bg)",
      overflow: "hidden",
      // Flex sloupec nahrazuje smazané pravidlo #root > div v App.css – bez něj
      // by vnitřní scroll kontejner AuthPage neměl omezenou výšku a overflow
      // hidden by obsah ořízl bez možnosti scrollu.
      display: "flex",
      flexDirection: "column",
    }}
  >
    {children}
  </div>
);

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <ConfirmProvider>
          <Routes>
            {/* ── Přihlášení a stavové stránky (AppShell bez spodní navigace;
                AuthPage si titulek přepíná přes useAppHeader) ── */}
            <Route
              path="/"
              element={
                <AppShell variant="user" showNav={false} title="LKS Apka">
                  <AuthPage />
                </AppShell>
              }
            />
            <Route
              path="/login"
              element={
                <AppShell variant="user" showNav={false} title="LKS Apka">
                  <AuthPage />
                </AppShell>
              }
            />
            <Route
              path="/email-verified"
              element={
                <AppShell variant="user" showNav={false} title="Ověření e-mailu">
                  <EmailVerified />
                </AppShell>
              }
            />
            <Route
              path="/password-reset-complete"
              element={
                <AppShell variant="user" showNav={false} title="Změna hesla">
                  <PasswordResetComplete />
                </AppShell>
              }
            />

            {/* ── Uživatelská část (AppShell user + spodní navigace) ── */}
            <Route
              element={
                <RequireAuth>
                  <AppShell variant="user" />
                </RequireAuth>
              }
            >
              <Route
                path="/user"
                element={<Screen title="LKS Apka"><UserDashboard /></Screen>}
              />
              <Route
                path="/results"
                element={<Screen title="Výsledky"><UserResultsPage /></Screen>}
              />
              <Route
                path="/profile"
                element={<Screen title="Profil"><UserProfilePage /></Screen>}
              />
              <Route
                path="/updateprofile"
                element={
                  <Screen title="Úprava profilu" back={{ to: "/profile" }}>
                    <UpdateProfile />
                  </Screen>
                }
              />
              <Route
                path="/about"
                element={
                  <Screen title="O aplikaci" back={{ to: "/profile" }}>
                    <AboutApp />
                  </Screen>
                }
              />
              <Route
                path="/gdpr"
                element={
                  <Screen title="Ochrana údajů" back={{ to: "/profile" }}>
                    <GDPRPage />
                  </Screen>
                }
              />
              <Route
                path="/payment/:raceId"
                element={
                  <Screen title="Přihláška na závod" back={{ to: "/user" }}>
                    <PaymentRace />
                  </Screen>
                }
              />
              <Route
                path="/paymentmembership"
                element={
                  <Screen title="Platba členství" back={{ to: "/user" }}>
                    <PaymentMembership />
                  </Screen>
                }
              />
            </Route>

            {/* ── Prohlížeče propozic a mapy: stejná hlavička a spodní navigace,
                ale AppShell ve fullscreen režimu (main bez paddingu, obsah
                vyplní plochu mezi hlavičkou a navigací, posouvá se sám).
                Titulek, podtitulek a akce si nastavují obrazovky přes
                useAppHeader (závisí na načtených datech). Chodí se sem
                z dashboardu i z přihlášky – Zpět proto historií (fallback
                /user řeší AppShell). ── */}
            <Route
              element={
                <RequireAuth>
                  <AppShell variant="user" fullscreen back="history" />
                </RequireAuth>
              }
            >
              <Route path="/pdfviewer/:raceId" element={<PdfViewer />} />
              <Route path="/mapviewer/:raceId" element={<MapViewer />} />
            </Route>

            {/* ── Administrace (role admin/superadmin) ── */}
            <Route
              path="/admin"
              element={
                <RequireRole roles={["admin", "superadmin"]}>
                  <AppShell variant="admin" />
                </RequireRole>
              }
            >
              <Route index element={<Screen title="Administrace"><AdminDashboard /></Screen>} />
              <Route
                path="members"
                element={
                  <Screen title="Seznam střelců" back={{ to: "/admin" }}>
                    <MembersList />
                  </Screen>
                }
              />
              <Route
                path="shootingRanges"
                element={
                  <Screen title="Seznam střelnic" back={{ to: "/admin" }}>
                    <ShootingRanges />
                  </Screen>
                }
              />
              <Route
                path="payments"
                element={
                  <Screen title="Příchozí platby" back={{ to: "/admin" }}>
                    <Payments />
                  </Screen>
                }
              />
              <Route
                path="manualpayments"
                element={
                  <Screen title="Párování plateb" back={{ to: "/admin" }}>
                    <ManualPayments />
                  </Screen>
                }
              />
              <Route
                path="races"
                element={
                  <Screen title="Závody" back={{ to: "/admin" }}>
                    <Races />
                  </Screen>
                }
              />
              <Route
                path="membershipPricing"
                element={
                  <Screen title="Členské poplatky" back={{ to: "/admin" }}>
                    <MembershipPricing />
                  </Screen>
                }
              />
              <Route
                path="registrations"
                element={
                  <Screen title="Přihlášky" back={{ to: "/admin" }}>
                    <AdminRegistrations />
                  </Screen>
                }
              />
              <Route
                path="resultsupload"
                element={
                  <Screen title="Import výsledků" back={{ to: "/admin" }}>
                    <AdminResultsUpload />
                  </Screen>
                }
              />
              <Route
                path="shootoffs"
                element={
                  <Screen title="Rozstřely" back={{ to: "/admin" }}>
                    <AdminShootOffs />
                  </Screen>
                }
              />
              <Route
                path="overview"
                element={
                  <Screen title="Přehled systému" back={{ to: "/admin" }}>
                    <AdminOverview />
                  </Screen>
                }
              />
              <Route
                path="targets"
                element={
                  <Screen title="Databáze terčů" back={{ to: "/admin" }}>
                    <TargetsAdmin />
                  </Screen>
                }
              />
              <Route
                path="generalka"
                element={
                  <Screen title="Generálka – testovací závod" back={{ to: "/admin" }}>
                    <AdminRehearsal />
                  </Screen>
                }
              />
            </Route>

            {/* Nastavení live scoringu – edituje ho i hlavní rozhodčí bez admin
                role (LiveRaceSetup canEditLive), proto jen RequireAuth;
                oprávnění si hlídá obrazovka. Zpřesnění zámku = krok 11. */}
            <Route
              path="/admin/race-scoring/:raceId"
              element={
                <RequireAuth>
                  <PreloadLiveScreens withXlsx />
                  <AppShell
                    variant="admin"
                    title="Nastavení živého bodování"
                    back="history"
                  >
                    <LiveRaceSetup />
                  </AppShell>
                </RequireAuth>
              }
            />

            {/* Živé výsledky – používají i rozhodčí (JudgeView sem naviguje),
                proto jen RequireAuth; oprávnění si hlídá obrazovka sama. */}
            <Route
              path="/admin/race-results/:raceId"
              element={
                <RequireAuth>
                  <PreloadLiveScreens withXlsx />
                  <LiveResultsRoute />
                </RequireAuth>
              }
            />

            {/* ── Live scoring (nemění se až do kroků 12–13) ── */}
            {/* Pád obrazovky rozhodčího v terénu: hláška se „Zkusit znovu“, ne bílá stránka */}
            <Route
              path="/judge/:raceId"
              element={
                <Bare>
                  <PreloadLiveScreens />
                  <RouteErrorBoundary resetKey="/judge">
                    <JudgeView />
                  </RouteErrorBoundary>
                </Bare>
              }
            />
            {/* Tabule nemá vlastní offline indikaci – overlay ji dodává zvenčí.
                Pád tabule na TV: chybová hláška a za 30 s nový pokus sám
                (fáze 4a, E3 – dřív bílá obrazovka do obnovení). */}
            <Route
              path="/display/:raceId"
              element={<Bare><OfflineOverlay /><RouteErrorBoundary resetKey="/display" autoRetryMs={DISPLAY_CRASH_RETRY_MS}><React.Suspense fallback={<RouteLoading />}><DisplayView /></React.Suspense></RouteErrorBoundary></Bare>}
            />
            <Route
              path="/display"
              element={<Bare><OfflineOverlay /><RouteErrorBoundary resetKey="/display" autoRetryMs={DISPLAY_CRASH_RETRY_MS}><React.Suspense fallback={<RouteLoading />}><DisplayView /></React.Suspense></RouteErrorBoundary></Bare>}
            />

            {/* ── 404 ── */}
            <Route path="*" element={<Bare><NotFound /></Bare>} />
          </Routes>
        </ConfirmProvider>
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
